import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { toast } from 'react-toastify';
import Lottie from 'react-lottie';

import { ContainerModal } from './style.js';
import Loading from '../../../assets/loading.json';
import ButtonDefault from '../../ButtonDefault/index.js';
import api from '../../../services/api.js';

const ModalViewChannel = ({
  modalOpen,
  handleClose,
  channelId,
  channelName
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [channelData, setChannelData] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0,
    has_more_pages: false
  });

  const loadingAnimation = {
    loop: true,
    autoplay: true,
    animationData: Loading
  };

  const fetchChannelUsers = async (page = 1) => {
    if (!channelId) return;
    
    setIsLoading(true);
    try {
      const response = await api.get(`sales_channel/users?id=${channelId}&page=${page}`);
      
      if (response.data && response.data.data) {
        setChannelData(response.data.data.sales_channel);
        setUsersList(response.data.data.users);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários do canal:', error);
      toast.error('Erro ao carregar usuários do canal');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (modalOpen && channelId) {
      fetchChannelUsers(1);
    }
  }, [modalOpen, channelId]);

  const formatCpfCnpj = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4').replace(/-$/, '');
    } else {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5').replace(/-$/, '');
    }
  };

  const formatPhone = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
    } else {
      return cleaned.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusText = (status) => {
    return status === 'A' ? 'ATIVO' : 'INATIVO';
  };

  const getUserTypeText = (tipo) => {
    switch (tipo) {
      case '1':
        return 'ADMINISTRADOR';
      case '2':
        return 'USUÁRIO';
      case '3':
        return 'VENDEDOR';
      default:
        return 'NÃO DEFINIDO';
    }
  };

  const goToPage = (page) => {
    fetchChannelUsers(page);
  };

  const goToFirstPage = () => goToPage(1);
  const goToPreviousPage = () => {
    if (pagination.current_page > 1) {
      goToPage(pagination.current_page - 1);
    }
  };
  const goToNextPage = () => {
    if (pagination.has_more_pages) {
      goToPage(pagination.current_page + 1);
    }
  };
  const goToLastPage = () => goToPage(pagination.last_page);

  return (
    <Modal show={modalOpen} onHide={handleClose} size="xl" centered>
      <ContainerModal>
        <Modal.Header closeButton>
          <Modal.Title>
            Visualizar Canal de Venda: {channelName}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {isLoading ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: '300px'
            }}>
              <Lottie options={loadingAnimation} style={{ width: '150px', height: '150px' }} />
              <h6 style={{ marginTop: '20px' }}>Carregando dados do canal...</h6>
            </div>
          ) : (
            <>
              {/* Informações do Canal */}
              {channelData && (
                <div className="channel-info mb-4">
                  <h5 style={{ color: 'var(--primary-color)', marginBottom: '15px' }}>
                    Informações do Canal
                  </h5>
                  <div className="row">
                    <div className="col-md-4">
                      <strong>Código:</strong> {channelData.ID_DM017}
                    </div>
                    <div className="col-md-4">
                      <strong>Nome:</strong> {channelData.NM_CANAL}
                    </div>
                    <div className="col-md-4">
                      <strong>Status:</strong> 
                      <span style={{ 
                        color: channelData.IN_SITUACAO === 'A' ? '#5CB176' : '#EE5E52',
                        fontWeight: 'bold',
                        marginLeft: '5px'
                      }}>
                        {getStatusText(channelData.IN_SITUACAO)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de Usuários */}
              <div className="users-section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 style={{ color: 'var(--primary-color)', marginBottom: 0 }}>
                    Usuários Vinculados ao Canal
                  </h5>
                  {pagination.total > 0 && (
                    <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {pagination.total} usuário{pagination.total !== 1 ? 's' : ''} encontrado{pagination.total !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {usersList.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Nome</th>
                            <th>E-mail</th>
                            <th>CPF/CNPJ</th>
                            <th>Telefone</th>
                            <th>Tipo</th>
                            <th>Status</th>
                            <th>Cadastro</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersList.map((user) => (
                            <tr key={user.id}>
                              <td>{user.name}</td>
                              <td>{user.email}</td>
                              <td>{formatCpfCnpj(user.NR_CPFCNPJ)}</td>
                              <td>{formatPhone(user.NR_FONE)}</td>
                              <td>{getUserTypeText(user.IN_TIPO)}</td>
                              <td>
                                <span style={{ 
                                  color: user.IN_SITUACAO === 'A' ? '#5CB176' : '#EE5E52',
                                  fontWeight: 'bold'
                                }}>
                                  {getStatusText(user.IN_SITUACAO)}
                                </span>
                              </td>
                              <td>{formatDate(user.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* Paginação */}
                    {pagination.total > pagination.per_page && (
                      <div className="pagination-section mt-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                            Exibindo {pagination.from} a {pagination.to} de {pagination.total} registros
                          </span>
                          
                          <div className="pagination-controls d-flex align-items-center gap-2">
                            <ButtonDefault
                              Width={'60px'}
                              Margin={'0'}
                              Text="<<"
                              onClick={goToFirstPage}
                              disabled={pagination.current_page === 1}
                              style={{ 
                                opacity: pagination.current_page === 1 ? 0.5 : 1,
                                fontSize: '12px'
                              }}
                            />
                            <ButtonDefault
                              Width={'60px'}
                              Margin={'0'}
                              Text="<"
                              onClick={goToPreviousPage}
                              disabled={pagination.current_page === 1}
                              style={{ 
                                opacity: pagination.current_page === 1 ? 0.5 : 1,
                                fontSize: '12px'
                              }}
                            />
                            
                            <span style={{ 
                              margin: '0 10px', 
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}>
                              Página {pagination.current_page} de {pagination.last_page}
                            </span>
                            
                            <ButtonDefault
                              Width={'60px'}
                              Margin={'0'}
                              Text=">"
                              onClick={goToNextPage}
                              disabled={!pagination.has_more_pages}
                              style={{ 
                                opacity: !pagination.has_more_pages ? 0.5 : 1,
                                fontSize: '12px'
                              }}
                            />
                            <ButtonDefault
                              Width={'60px'}
                              Margin={'0'}
                              Text=">>"
                              onClick={goToLastPage}
                              disabled={!pagination.has_more_pages}
                              style={{ 
                                opacity: !pagination.has_more_pages ? 0.5 : 1,
                                fontSize: '12px'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px',
                    color: 'var(--text-secondary)'
                  }}>
                    <h6>Nenhum usuário encontrado para este canal</h6>
                    <p>Este canal ainda não possui usuários vinculados.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <ButtonDefault
            Width={'120px'}
            Margin={'0'}
            Text="Fechar"
            onClick={handleClose}
          />
        </Modal.Footer>
      </ContainerModal>
    </Modal>
  );
};

export default ModalViewChannel;
